import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useOfflineFirstQuery } from '../../../hooks/useOfflineFirstQuery'
import { apiClient } from '../../../api/client'

type Claim = {
  id: string
  status: string
  service_date: string
  total_amount: string
  payer_name: string
  client_name: string
}

export default function ClaimsListPage() {
  const { data, isFetching, error, refetch } = useOfflineFirstQuery<Claim[]>({
    queryKey: ['claims'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/billing/claims/')
      return res.data as Claim[]
    },
    cacheKey: 'claims:list',
    staleTime: 1000 * 60,
  })

  const rows = useMemo(() => data ?? [], [data])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Claims</h1>
        <div className="flex gap-2">
          <Link
            to="/app/billing/claims/generate"
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
          >
            Generate from Session
          </Link>
          <button
            onClick={() => refetch()}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {(error as Error).message}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isFetching && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                  Loading...
                </td>
              </tr>
            )}
            {rows.map((claim) => (
              <tr key={claim.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{claim.service_date}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{claim.client_name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{claim.payer_name}</td>
                <td className="px-4 py-3 text-sm">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                    {claim.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{claim.total_amount}</td>
                <td className="px-4 py-3 text-right text-sm">
                  <Link to={`/app/billing/claims/${claim.id}`} className="text-primary-600 hover:text-primary-500">
                    View
                  </Link>
                </td>
              </tr>
            ))}
            {!isFetching && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                  No claims yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
